import { Box, LinearProgress, Typography, styled } from "@mui/material";
import React, { ReactNode } from "react";
import { toPercent } from "@helpers";
import type { ClassificationBreakdownData } from "@components";

const ColorSwatch = styled(Box)<{ color: string }>(({ color }) => ({
  backgroundColor: color,
  width: 12,
  height: 12,
  borderRadius: "50%",
  flexShrink: 0,
}));

const StyledLinearProgress = styled(LinearProgress)(() => ({
  height: 8,
  borderRadius: 8,
}));

export interface ClassificationLegendItemProps {
  item: ClassificationBreakdownData;
  rootTotalValue: number;
  isSelected?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick?: () => void;
  isLast?: boolean;
  beforeEl?: ReactNode;
}

export const LegendItem = ({
  item,
  rootTotalValue,
  isSelected = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isLast = false,
  beforeEl,
}: ClassificationLegendItemProps) => {
  const { fillColor: color, name = "", value, hasChildren } = item;
  const safeRoot = rootTotalValue > 0 ? rootTotalValue : 1;
  const ratio = value / safeRoot;
  const barPercent = Math.min(100, Math.max(0, ratio * 100));

  const percentLabel =
    ratio < 0.0001
      ? toPercent(ratio, "auto", 2, 3)
      : toPercent(ratio, "auto", 2, 2);

  return (
    <Box
      px={3}
      py={3}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      sx={(theme) => ({
        borderBottom: isLast
          ? "none"
          : `1px solid ${theme.palette.border.secondary}`,
        backgroundColor: isSelected
          ? theme.palette.action.hover
          : theme.palette.background.default,
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: theme.palette.background.subtle,
        },
      })}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        width="100%"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={2.5} minWidth={0} flex={1}>
          <ColorSwatch color={color} />
          {beforeEl}
          <Typography
            variant="body3"
            color={isSelected ? "primary.main" : "text.primary"}
            noWrap
            sx={{
              transition: "color 0.15s ease",
            }}
          >
            {name}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={1.5}>
        <StyledLinearProgress
          variant="determinate"
          value={barPercent}
          sx={(theme) => ({
            flex: 1,
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.primary.main,
            },
            backgroundColor: theme.palette.border.secondary,
          })}
        />
        <Typography
          variant="body3"
          color="text.secondary"
          whiteSpace="nowrap"
          textAlign="right"
          minWidth={68}
        >
          {percentLabel}
        </Typography>
      </Box>
    </Box>
  );
};
